import { YyyyMm, YyyyMmDd } from "@flexidao/dto";
import { Box, flexidaoGrey, formatNumber, Group, Paper, Stack, Text } from "@flexidao/ui-lib";
import { ReactElement, useMemo } from "react";
import {
    Bar,
    Cell,
    Legend,
    BarChart as RechartsBarChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

export type PositiveNegativeBarGroupChartElement = {
    key: YyyyMm | YyyyMmDd;
    value: number;
    projectedValue: number | null;
};

type PositiveNegativeBarGroupChartProps = {
    numDecimals: number;
    units: string;
    height: number;
    data: Array<PositiveNegativeBarGroupChartElement>;
    getColor: (value: number, isProjected: boolean) => string;
    legendItems: Array<{
        label: string;
        color: string;
    }>;
    dateFormatter: (value: any) => string;
    showProjections?: boolean;
};

const PositiveNegativeBarGroupChartCustomLegend = ({
    payload,
}: {
    payload?: Array<{ value: string; color: string }>;
}): ReactElement => (
    <Group spacing="lg" mt="sm">
        {payload?.map((entry, index) => (
            <Group key={`legend-${index}`} spacing="xs" align="center">
                <Box
                    w={10}
                    h={10}
                    sx={{
                        borderRadius: "50%",
                        backgroundColor: entry.color,
                        flexShrink: 0,
                    }}
                />
                <Text size="sm" component="span">
                    {entry.value}
                </Text>
            </Group>
        ))}
    </Group>
);

export const PositiveNegativeBarGroupChartCustomTooltip = ({
    label,
    value,
    projectedValue,
    showProjections,
}: {
    label: string;
    value: string;
    projectedValue: string;
    showProjections: boolean;
}): ReactElement | null => {
    return (
        <Paper>
            <Stack spacing="8px">
                <Group>
                    <Text weight="bold" size="sm" c="flexidaoGrey.5">
                        Period:
                    </Text>
                    <Group spacing={4} position="right" sx={{ flexGrow: 1 }}>
                        <Text size="sm" weight="bold" data-id="bar-chart-tooltip--period">
                            {label}
                        </Text>
                    </Group>
                </Group>
                <Group>
                    <Text weight="bold" size="sm" c="flexidaoGrey.5">
                        Value:
                    </Text>
                    <Group spacing={4} position="right" sx={{ flexGrow: 1 }}>
                        <Text size="sm" weight="bold" data-id="bar-chart-tooltip--value">
                            {value}
                        </Text>
                    </Group>
                </Group>
                {showProjections && (
                    <Group>
                        <Text weight="bold" size="sm" c="flexidaoGrey.5">
                            Projected value:
                        </Text>
                        <Group spacing={4} position="right" sx={{ flexGrow: 1 }}>
                            <Text
                                size="sm"
                                weight="bold"
                                data-id="bar-chart-tooltip--projected-value"
                            >
                                {projectedValue}
                            </Text>
                        </Group>
                    </Group>
                )}
            </Stack>
        </Paper>
    );
};

export const PositiveNegativeBarGroupChart = ({
    getColor,
    units,
    numDecimals,
    height,
    data,
    legendItems,
    dateFormatter,
    showProjections = false,
}: PositiveNegativeBarGroupChartProps): ReactElement => {
    const valueFormatter = (v: number): string =>
        formatNumber(v, {
            numDecimals,
            units,
            useNbSp: true,
        });

    // eslint-disable-next-line no-magic-numbers
    const minTickGap = useMemo(() => data.length / 5, [data]);

    return (
        <ResponsiveContainer width="100%" height={height}>
            <RechartsBarChart data={data}>
                {/* Axes */}
                <XAxis
                    dataKey="key"
                    tickFormatter={dateFormatter}
                    color={flexidaoGrey[2]}
                    fontSize="10px"
                    minTickGap={minTickGap}
                />
                <YAxis
                    tickFormatter={valueFormatter}
                    color={flexidaoGrey[2]}
                    fontSize="10px"
                    orientation="right"
                />

                {/* Chart */}
                <Bar dataKey="value">
                    {data.map(
                        (entry, index): ReactElement => (
                            <Cell
                                key={`historical-cell-${index}`}
                                fill={getColor(entry.value, false)}
                            />
                        ),
                    )}
                </Bar>
                {showProjections && (
                    <Bar dataKey="projectedValue">
                        {data.map(
                            (entry, index): ReactElement => (
                                <Cell
                                    key={`projected-cell-${index}`}
                                    fill={getColor(entry.projectedValue ?? 0, true)}
                                />
                            ),
                        )}
                    </Bar>
                )}

                {/* Reference Line */}
                <ReferenceLine y={0} stroke={flexidaoGrey[2]} />

                {/* Tooltip */}
                <Tooltip
                    cursor={{ fill: "transparent" }}
                    content={({ payload }): ReactElement | null => {
                        const label = payload?.[0]?.payload.key;
                        const value = payload?.[0]?.value ?? "-";
                        const projectedValue = payload?.[0]?.payload?.projectedValue ?? "-";

                        const formattedLabel = dateFormatter(label);
                        const formattedValue = valueFormatter(Number(value));
                        const formattedProjectedValue = valueFormatter(projectedValue);

                        return (
                            <PositiveNegativeBarGroupChartCustomTooltip
                                label={formattedLabel}
                                value={formattedValue}
                                projectedValue={formattedProjectedValue}
                                showProjections={showProjections}
                            />
                        );
                    }}
                />

                {/* Legend */}
                <Legend
                    align="left"
                    payload={legendItems.map(({ label, color }) => ({
                        value: label,
                        type: "circle",
                        color,
                    }))}
                    content={<PositiveNegativeBarGroupChartCustomLegend />}
                />
            </RechartsBarChart>
        </ResponsiveContainer>
    );
};
